import { useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { getAllTransactionCategories } from '@/features/reports/reports.selectors'
import { plaidCategoryPhrase } from '@/features/onboarding/shared/plaidCategory'
import {
  categorizeMerchant,
  selectMerchantReview,
  type CategorizeResult,
  type MerchantReview,
  type MerchantReviewTransaction,
} from '../merchantReview.slice'

type CategoryMap = Record<number, { name: string }>

const categoryName = (
  categories: CategoryMap,
  categoryId: number | null
): string => (categoryId === null ? '' : (categories[categoryId]?.name ?? ''))

const chosenCategory = (
  transaction: MerchantReviewTransaction,
  rowCategories: Record<number, number>,
  groupCategoryId: number | null,
  groupTouched: boolean
): number | null => {
  const chosen = rowCategories[transaction.id]
  if (chosen !== undefined) return chosen
  if (groupTouched) return groupCategoryId
  return transaction.suggestedCategoryId ?? groupCategoryId
}

const HELP_CLAUSE =
  'Help me understand the transaction categories and which business expense fits, then show me the card to pick one.'

const toSuggestionSummary = (
  group: MerchantReview,
  nameOf: (categoryId: number | null) => string
): string => {
  const counts = new Map<number, number>()
  for (const transaction of group.transactions) {
    const categoryId = transaction.suggestedCategoryId
    if (categoryId === null) continue
    counts.set(categoryId, (counts.get(categoryId) ?? 0) + 1)
  }

  return [...counts]
    .sort(([, a], [, b]) => b - a)
    .map(([categoryId, count]) => `${nameOf(categoryId)} for ${count}`)
    .join(' and ')
}

const toUnsureSentence = (
  group: MerchantReview,
  nameOf: (categoryId: number | null) => string
): string => {
  const summary = toSuggestionSummary(group, nameOf)
  if (summary)
    return `I'm not sure about ${group.merchant}. Heard suggests ${summary}. ${HELP_CLAUSE}`

  if (group.prior)
    return `I'm not sure about ${group.merchant}. Heard's standing suggestion for this merchant is ${nameOf(group.prior.categoryId)}. ${HELP_CLAUSE}`

  if (group.plaidCategory)
    return `I'm not sure which category ${group.merchant} belongs to. The bank classifies it as ${plaidCategoryPhrase(group.plaidCategory.code)}. ${HELP_CLAUSE}`

  return `I'm not sure which category ${group.merchant} belongs to. ${HELP_CLAUSE}`
}

const toAssignments = (
  group: MerchantReview,
  categoryOf: (transaction: MerchantReviewTransaction) => number | null,
  personalRows: Set<number>
): Array<{ transactionId: number; categoryId: number }> =>
  group.transactions.flatMap((transaction) => {
    const categoryId = categoryOf(transaction)
    return categoryId === null || personalRows.has(transaction.id)
      ? []
      : [{ transactionId: transaction.id, categoryId }]
  })

const everyRowIsSettled = (
  group: MerchantReview,
  categoryFor: (transaction: MerchantReviewTransaction) => number | null,
  personalRows: Set<number>
): boolean =>
  group.transactions.length > 0 &&
  group.transactions.every(
    (transaction) =>
      personalRows.has(transaction.id) || categoryFor(transaction) !== null
  )

export interface MerchantCard {
  group?: MerchantReview
  groupCategoryId: number | null
  primaryLabel: string
  unsureSentence: string
  nameOf: (categoryId: number | null) => string
  categoryOf: (transaction: MerchantReviewTransaction) => number | null
  setGroupCategory: (categoryId: number | null) => void
  setRowCategory: (transactionId: number, categoryId: number | null) => void
  isRowPersonal: (transactionId: number) => boolean
  toggleRowPersonal: (transactionId: number) => void
  canSave: boolean
  save: (
    categoryFor: (transaction: MerchantReviewTransaction) => number | null
  ) => Promise<CategorizeResult | undefined>
  saving: boolean
  setSaving: (saving: boolean) => void
}

export const useMerchantCard = (groupKey: string): MerchantCard => {
  const dispatch = useAppDispatch()
  const group = useReselector(selectMerchantReview, groupKey)
  const categoriesById = useReselector(getAllTransactionCategories)

  const [groupCategoryId, setGroupCategoryId] = useState<number | null>(null)
  const [rowCategories, setRowCategories] = useState<Record<number, number>>({})
  const [groupTouched, setGroupTouched] = useState(false)
  const [personalRows, setPersonalRows] = useState<Set<number>>(new Set())
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)

  const suggestedCategoryId = group?.suggestedCategoryIds[0] ?? null
  useEffect(() => {
    setRowCategories({})
    setPersonalRows(new Set())
    setGroupTouched(false)
    setGroupCategoryId(suggestedCategoryId)
  }, [groupKey, suggestedCategoryId])

  const nameOf = (categoryId: number | null): string =>
    categoryName(categoriesById, categoryId)

  const categoryOf = (transaction: MerchantReviewTransaction): number | null =>
    chosenCategory(transaction, rowCategories, groupCategoryId, groupTouched)

  const anyRowDiffers = (group?.transactions ?? []).some(
    (transaction) =>
      personalRows.has(transaction.id) ||
      categoryOf(transaction) !== groupCategoryId
  )

  const save = async (
    categoryFor: (transaction: MerchantReviewTransaction) => number | null
  ): Promise<CategorizeResult | undefined> => {
    if (!group || !everyRowIsSettled(group, categoryFor, personalRows))
      return undefined
    if (savingRef.current) return undefined

    const assignments = toAssignments(group, categoryFor, personalRows)
    const personalTransactionIds = group.transactions
      .filter((transaction) => personalRows.has(transaction.id))
      .map((transaction) => transaction.id)

    savingRef.current = true
    setSaving(true)
    const result = await dispatch(
      categorizeMerchant(group.groupKey, {
        assignments,
        personalTransactionIds,
      })
    )
    if (!result) {
      savingRef.current = false
      setSaving(false)
    }
    return result
  }

  return {
    group,
    groupCategoryId,
    primaryLabel: anyRowDiffers
      ? 'Save'
      : `Confirm ${nameOf(groupCategoryId)} for all`,
    unsureSentence: group ? toUnsureSentence(group, nameOf) : '',
    nameOf,
    categoryOf,
    setGroupCategory: (categoryId: number | null): void => {
      setGroupCategoryId(categoryId)
      setGroupTouched(true)
    },
    setRowCategory: (
      transactionId: number,
      categoryId: number | null
    ): void => {
      if (categoryId === null) return
      setRowCategories((current) => ({
        ...current,
        [transactionId]: categoryId,
      }))
    },
    isRowPersonal: (transactionId: number): boolean =>
      personalRows.has(transactionId),
    toggleRowPersonal: (transactionId: number): void => {
      setPersonalRows((current) => {
        const next = new Set(current)
        if (!next.delete(transactionId)) next.add(transactionId)
        return next
      })
    },
    canSave:
      group !== undefined && everyRowIsSettled(group, categoryOf, personalRows),
    save,
    saving,
    setSaving: (next: boolean): void => {
      savingRef.current = next
      setSaving(next)
    },
  }
}
